import * as React from 'react'
import { customerMarketShopfrontApi, Site } from '@envato/foundation-types'

import {
  ICommonLocation,
  MakeSearchLink,
  ILocationParams
} from '../../../common/utils/location/types'

import ArticleBlock from '../../components/categoryPage/articleBlock'
import CategoriesWithIconsBlock from '../../components/categoryPage/categoriesWithIconsBlock'
import CategoriesWithCountBlock from '../../components/categoryPage/categoriesWithCountBlock'
import CategoryIconsBlock from '../../components/categoryPage/categoryIconsBlock'
import PopularSearchesBlock from '../../components/categoryPage/popularSearchesBlock'
import CategoryPageWrapper from '../../components/categoryPage/categoryPageWrapper'
import PromoItemsBlock from '../../components/categoryPage/promoItemsBlock'
import ElementsBannerBlock from '../../components/categoryPage/elementsBannerBlock'
import ElementsPromoItemsBlock from '../../components/categoryPage/elementsPromoItemsBlock'
import SearchFiltersBlock from '../../components/categoryPage/searchFiltersBlock'
import FullWidthBannerBlock from '../../components/categoryPage/fullWidthBannerBlock'
import ItemsBlock from '../../components/categoryPage/itemsBlock'
import SearchBannersWithIconsBlock from '../../components/categoryPage/searchBannersWithIconsBlock'
import TextSearchFiltersBlock from '../../components/categoryPage/textSearchFiltersBlock'
import EmptyResultsPlaceholder from '../../components/emptyResultsPlaceholder'
import PageHeader from '../../components/pageHeader'
import SurveyBanner from '../../components/surveyBanners/surveyBanner'
import TabbedTableBlock from '../../components/categoryPage/tabbedTableBlock'
import { Props } from '../../containers/categoryPageContainer/categoryPageContainer'
import { hasFeature } from '../../../common/utils/features'
import { getPageViewCount } from '../../../common/utils/tracking/trackPageViewCount'
import { getConfigBySite } from '../../../config/sites'

interface IBlockComponentsMap {
  [key: string]: React.ComponentType<IBlockMatchedComponent>
}

interface IRenderBlocks {
  blocks: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[]
  currentSite: Site
  location: ICommonLocation
  makeSearchLink: MakeSearchLink
  locationParams: ILocationParams
  lookupMap?: IBlockComponentsMap
}

export interface IBlockMatchedComponent
  extends customerMarketShopfrontApi.categoryPage.ICategoryPageBlock {
  currentSite: Site
  location: ICommonLocation
  locationParams: ILocationParams
  makeSearchLink: MakeSearchLink
}

const blockComponentsMap: IBlockComponentsMap = {
  ItemsBlock,
  ArticleBlock,
  CategoriesWithIconsBlock,
  CategoriesWithCountBlock,
  CategoryIconsBlock,
  SearchBannersWithIconsBlock,
  PopularSearchesBlock,
  PromoItemsBlock,
  ElementsBannerBlock,
  ElementsPromoItemsBlock,
  SearchFiltersBlock,
  TextSearchFiltersBlock,
  FullWidthBannerBlock,
  TabbedTableBlock
}

const isScheduledForNow = (
  block: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock<any>
) => {
  if (block.scheduled) {
    const currentDate = new Date()
    const startDate = new Date(block.scheduled.startsAt)
    const endDate = new Date(block.scheduled.endsAt)
    return startDate < currentDate && currentDate < endDate
  } else {
    return false
  }
}

export const renderBlocks = ({
  blocks,
  currentSite,
  location,
  makeSearchLink,
  locationParams,
  lookupMap = blockComponentsMap
}: IRenderBlocks) => {
  const shouldShowAllScheduled = hasFeature(
    'showAllScheduledBlocks',
    currentSite,
    location
  )

  const shouldShowBlock = (
    block: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock<any>
  ): boolean => {
    const hasNoSchedule = !block.scheduled
    const shouldShowBasedOnSchedule =
      hasNoSchedule || isScheduledForNow(block) || shouldShowAllScheduled

    return shouldShowBasedOnSchedule
  }

  const componentForBlock = (
    block: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock<any>,
    index: number
  ) => {
    const MatchedComponent = lookupMap[block.componentType]
    return MatchedComponent ? (
      <MatchedComponent
        key={block.componentType + index}
        currentSite={currentSite}
        location={location}
        locationParams={locationParams}
        makeSearchLink={makeSearchLink}
        {...block}
      />
    ) : null
  }

  function isNotEmpty<T>(component: T | null): component is T {
    return !!component
  }

  return blocks
    .filter(shouldShowBlock)
    .map(componentForBlock)
    .filter(isNotEmpty)
}

const renderSurveyBanner = (currentSite: Site) => {
  const survey = getConfigBySite(currentSite, 'surveys.category')
  const shortName = getConfigBySite(currentSite, 'abbreviation').toLowerCase()

  if (
    !!survey &&
    getPageViewCount(currentSite, 'category') >= survey.minSearchCountToShow
  ) {
    return (
      <SurveyBanner
        siteId={shortName}
        pageType="category"
        surveyUrl={survey.url}
        surveyText={survey.text}
        surveyLink={survey.linkText}
      />
    )
  }

  return null
}

const CategoryPage = (props: Props) => {
  const {
    location,
    metadata,
    topArticle,
    category,
    blocks,
    blocksB,
    blocksC,
    blocksD,
    currentSite,
    makeSearchLink,
    experiments,
    experimentsReady,
    locationParams,
    dataRequestFailed,
    isLoading
  } = props

  const isCategoryPageBlocksB = hasFeature(
    'categoryPageBlocksB',
    currentSite,
    location,
    experiments
  )

  const isCategoryPageBlocksC = hasFeature(
    'categoryPageBlocksC',
    currentSite,
    location,
    experiments
  )

  const isCategoryPageBlocksD = hasFeature(
    'categoryPageBlocksD',
    currentSite,
    location,
    experiments
  )

  let selectedBlocks

  if (isCategoryPageBlocksB) {
    selectedBlocks = blocksB
  } else if (isCategoryPageBlocksC) {
    selectedBlocks = blocksC
  } else if (isCategoryPageBlocksD) {
    selectedBlocks = blocksD
  } else {
    selectedBlocks = blocks
  }

  // For author driven price experiment
  // Used to determine whether show sales block Variant 0/Variant 1
  const hasShowPromoItemsBlock = hasFeature(
    'showPromoItemsBlock',
    currentSite,
    location,
    experiments
  )

  if (!hasShowPromoItemsBlock) {
    selectedBlocks = selectedBlocks.filter(
      block => block.componentType !== 'PromoItemsBlock'
    )
  }

  const shouldShowError =
    !isLoading && (dataRequestFailed || !selectedBlocks.length)

  return (
    <CategoryPageWrapper
      experimentsReady={experimentsReady}
      isLoading={isLoading}
      currentSite={currentSite}
    >
      {!shouldShowError && metadata && (
        <PageHeader
          heading={metadata.heading}
          subHeading={metadata.sub_heading}
          topArticle={topArticle}
          category={category}
          currentSite={currentSite}
          locationParams={locationParams}
          makeSearchLink={makeSearchLink}
          showAutoSuggest
        />
      )}

      {shouldShowError && (
        <EmptyResultsPlaceholder
          heading="We're experiencing a technical issue"
          subHeading="Please refresh or try again later"
        />
      )}

      {renderSurveyBanner(currentSite)}

      {!shouldShowError &&
        renderBlocks({
          blocks: selectedBlocks,
          currentSite,
          location,
          makeSearchLink,
          locationParams
        })}
    </CategoryPageWrapper>
  )
}

export default CategoryPage
